/** @jsx jsx */

import React, { ReactElement } from 'react';
import _ from 'lodash';

import { jsx } from '@emotion/core';

import EditorWithPreviewLayout from 'app/developer-v3/layouts/EditorWithPreviewLayout';
import { NEW_SERVICE_FIELD } from 'app/developer-v3/constants';
import { cleanObjectOfEmptyValues } from 'app/common/CommonUtils';
import {
  convertDeepLinkMetaKeyToCamelCase,
  createValidator,
  setReadOnly,
  getDeepLinkMetaInfoFromQuery,
  getInputFieldIndex,
  getInputFields,
  getPerformFromService,
  getTriggerUrl,
  isNewServiceField,
  normalizeField,
  transformInputToFormatic,
  transformInputToSchema,
  updateServiceInputFields,
  validateDisallowed,
  validateDynamicDropdown,
  validateMin,
  validateRequired,
  validateSchemaKey,
} from 'app/developer-v3/utils';

import CTA from './CTA';
import { getFormFields } from './fieldTemplates';

import type {
  ErrorList,
  ValidatorFormValues,
} from 'app/developer-v3/types/validator';
import type {
  FieldOrFunctionSchema,
  StaticDropdown,
} from 'app/developer-v3/platformSchema/field';
import type {
  FormFieldTemplate,
  FormifiedFieldSchema,
  QueryHighlight,
  TriggerParams,
} from 'app/developer-v3/types';
import type { TriggerSchema } from 'app/developer-v3/platformSchema/trigger';

type Props = {
  trigger: TriggerSchema;
  children: ReactElement;
  isReadOnly: boolean;
  isTriggerSetup: boolean;
  saveTriggerUpdates: (
    trigger: TriggerSchema,
    shouldRedirect?: boolean
  ) => void;
  params: { inputKey?: string } & TriggerParams;
  query: QueryHighlight;
};

type State = {
  currentTrigger: TriggerSchema;
  formifiedField: FormifiedFieldSchema | {};
  confirmedSave: boolean;
  errors: ErrorList;
  attemptedSave: boolean;
};

const addPlaceholderInputField = service => {
  const placeholderInputField = {
    key: NEW_SERVICE_FIELD,
    label: '',
    type: 'string',
  };
  const fields = getInputFields(service).concat(placeholderInputField);

  return updateServiceInputFields(service, fields);
};

class TriggerInputs extends React.Component<Props, State> {
  validateValues: (values: ValidatorFormValues) => ErrorList;
  inputFormFields: FormFieldTemplate[];

  constructor(props: Props) {
    super(props);
    this.state = {
      currentTrigger: this.props.trigger,
      formifiedField: {},
      confirmedSave: false,
      errors: {},
      attemptedSave: false,
    };

    this.refreshValidation();
  }

  componentDidUpdate(prevProps: Props) {
    const prevPerform = getPerformFromService(prevProps.trigger);
    const perform = getPerformFromService(this.props.trigger);

    if (!_.isEqual(prevPerform, perform)) {
      this.setState({ currentTrigger: this.props.trigger });
    }
  }

  get disallowedKeys() {
    const fields = this.state.currentTrigger.operation?.inputFields || [];
    const field = this.getFormifiedInputByKey(this.props.params.inputKey);

    if (!field.key) {
      return _.map(fields, 'key');
    }

    const currentFieldKey = field.key;

    return _.chain(fields).reject({ key: currentFieldKey }).map('key').value();
  }

  refreshValidation = () => {
    this.inputFormFields = setReadOnly(
      getFormFields(this.disallowedKeys),
      this.props.isReadOnly
    );
    this.validateValues = createValidator(this.inputFormFields, [
      validateMin,
      validateSchemaKey,
      validateDisallowed,
      validateDynamicDropdown,
      validateRequired,
    ]);
  };

  setDropdown = (type: string, value: string | StaticDropdown) => {
    this.editFieldAndTrigger({
      ..._.omit(this.state.formifiedField, [
        type === 'dynamic' ? 'choices' : 'dynamic',
      ]),
      [type]: type === 'dynamic' ? cleanObjectOfEmptyValues(value) : value,
    });
  };

  toggleDropdown = (usingDropdown: boolean) => {
    if (!usingDropdown) {
      this.editFieldAndTrigger(
        _.omit(this.state.formifiedField, ['dynamic', 'choices'])
      );
    }
  };

  editFieldAndTrigger = (formValues: FormifiedFieldSchema) => {
    this.setState(previousState => {
      const { currentTrigger: oldTrigger } = previousState;
      const formifiedField = normalizeField(formValues, true);
      const schemafiedField = transformInputToSchema(formifiedField);
      const inputIndex = getInputFieldIndex(
        oldTrigger,
        this.props.params.inputKey
      );

      const updatedFields = getInputFields(oldTrigger).map((field, i) =>
        i === inputIndex ? schemafiedField : field
      );
      const errors = this.validateValues(formifiedField);

      return {
        formifiedField,
        currentTrigger: updateServiceInputFields(oldTrigger, updatedFields),
        errors,
      };
    });
  };

  getFormifiedInputByKey(
    fieldIndex: number | string | typeof undefined,
    trigger: TriggerSchema = this.state.currentTrigger
  ) {
    if (fieldIndex === undefined) {
      return {};
    }

    const fields = getInputFields(trigger);

    return transformInputToFormatic(
      (isNewServiceField(fieldIndex)
        ? _.last(fields)
        : fields[Number(fieldIndex)]) || {}
    );
  }

  createNewInputField = () => {
    const currentTrigger = addPlaceholderInputField(this.state.currentTrigger);
    const formifiedField = this.getFormifiedInputByKey(
      NEW_SERVICE_FIELD,
      currentTrigger
    );

    this.setState(
      {
        attemptedSave: false,
        confirmedSave: false,
        currentTrigger,
        errors: {},
        formifiedField,
      },
      () => this.refreshValidation()
    );
  };

  setInputField = (inputKey: string) => {
    if (inputKey === NEW_SERVICE_FIELD) {
      this.createNewInputField();
      return;
    }

    this.refreshValidation();

    this.setState({
      formifiedField: this.getFormifiedInputByKey(inputKey),
      confirmedSave: false,
    });
  };

  resetUnsavedEdits = () => {
    if (this.state.confirmedSave) return;

    // TODO: need to remove the placeholder field (which might look saved, but isn't)

    this.setState({
      currentTrigger: this.props.trigger,
      attemptedSave: false,
      formifiedField: {},
    });
  };

  isFormValid = (errors: ErrorList = this.state.errors) => _.isEmpty(errors);

  saveChanges = (skipConfirm: boolean | {} = false) => {
    const { currentTrigger, formifiedField } = this.state;
    const errors = _.isEmpty(formifiedField) // don't validate the field when we're not working on one
      ? {}
      : this.validateValues(formifiedField);

    // skipConfirm comes back as an event when used in an event handler
    if (skipConfirm === true) {
      this.props.saveTriggerUpdates(currentTrigger, false);
      return;
    }

    if (this.isFormValid(errors)) {
      this.setState({ confirmedSave: true }, async () => {
        await this.props.saveTriggerUpdates(currentTrigger);
        this.refreshValidation();
        this.setState({ attemptedSave: false });
      });
    } else {
      this.setState({ attemptedSave: true, errors });
    }
  };

  updateInputFields = (inputsFields: FieldOrFunctionSchema[]) => {
    this.setState(({ currentTrigger }) => ({
      currentTrigger: updateServiceInputFields(currentTrigger, inputsFields),
    }));
  };

  deleteInputField = (fieldIndex: string) => {
    const updatedFields = getInputFields(this.state.currentTrigger).filter(
      (_field, i) => i !== parseInt(fieldIndex, 10)
    );

    this.setState(
      ({ currentTrigger }) => ({
        currentTrigger: updateServiceInputFields(currentTrigger, updatedFields),
      }),
      () => {
        this.refreshValidation();
        this.saveChanges(true);
      }
    );
  };

  isSaveDisabled = ({ attemptedSave }: State) => {
    if (this.props.isReadOnly) return true;

    if (!attemptedSave) return false;

    return !this.isFormValid();
  };

  render() {
    const { props, state } = this;
    const deepLinkInfo = getDeepLinkMetaInfoFromQuery(props.query);

    const metaInfo =
      deepLinkInfo &&
      convertDeepLinkMetaKeyToCamelCase(deepLinkInfo, 'help_text');

    return (
      <React.Fragment>
        <EditorWithPreviewLayout
          service={state.currentTrigger}
          title="Form Editor"
        >
          <CTA
            apiUrl={`${getTriggerUrl(props.params)}/api`}
            isSetup={props.isTriggerSetup}
          />
          {React.cloneElement(React.Children.only(props.children), {
            attemptedSave: state.attemptedSave,
            deleteInputField: this.deleteInputField,
            errors: state.errors,
            formFields: this.inputFormFields,
            formifiedField: this.getFormifiedInputByKey(
              getInputFieldIndex(state.currentTrigger, props.params.inputKey)
            ),
            info: metaInfo,
            isFormValid: this.isFormValid(),
            isSaveDisabled: this.isSaveDisabled,
            onFormChange: this.editFieldAndTrigger,
            resetUnsavedEdits: this.resetUnsavedEdits,
            saveTriggerChanges: this.saveChanges,
            setDropdown: this.setDropdown,
            setInputField: this.setInputField,
            toggleDropdown: this.toggleDropdown,
            trigger: state.currentTrigger,
            updateInputFields: this.updateInputFields,
          })}
        </EditorWithPreviewLayout>
      </React.Fragment>
    );
  }
}

export default TriggerInputs;
